"use client";

import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  cardClick,
  expansionClick,
  navigationClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useCallback, useMemo, useRef } from "react";
import { CourseSpecialisationsCarousel } from "../../components/molecules/CourseSpecialisationsCarousel";
import CourseSpecialisationsDesktop from "../../components/molecules/CourseSpecialisationsDesktop";
import CourseSpecialisationsMobile from "../../components/molecules/CourseSpecialisationsMobile";

// API Metadata structure (from CMS)
interface APISpecialisationMetadata {
  id: string;
  title: string;
  status: boolean;
  priority: number;
  sub_title: string;
  description: string;
  // Desktop fields
  desktop_icon?: string;
  desktop_icon_alt?: string;
  desktop_hover_icon?: string;
  desktop_hover_icon_alt?: string;
  desktop_title_text_color?: string;
  desktop_active_card_color?: string;
  desktop_inactive_card_color?: string;
  desktop_description_gradient?: string;
  desktop_sub_title_text_color?: string;
  desktop_description_text_color?: string;
  desktop_active_title_text_color?: string;
  desktop_active_sub_title_text_color?: string;
  // Desktop gradient fields
  desktop_gradient_angle?: string;
  desktop_gradient_start_color?: string;
  desktop_gradient_end_color?: string;
  // Mobile Variant 1 (carousel) fields
  mobile_variant_1_icon?: string;
  mobile_variant_1_icon_alt?: string;
  mobile_variant_1_left_nav_icon?: string;
  mobile_variant_1_left_nav_icon_alt?: string;
  mobile_variant_1_right_nav_icon?: string;
  mobile_variant_1_right_nav_icon_alt?: string;
  mobile_variant_1_title_card_color?: string;
  mobile_variant_1_title_text_color?: string;
  mobile_variant_1_description_card_color?: string;
  mobile_variant_1_description_text_color?: string;
  // Mobile Variant 2 (accordion) fields
  mobile_variant_2_card_color?: string;
  mobile_variant_2_card_gradient?: string;
  mobile_variant_2_title_text_color?: string;
  mobile_variant_2_sub_title_text_color?: string;
  mobile_variant_2_description_text_color?: string;
  mobile_variant_2_accordion_element_color?: string;
  // Mobile Variant 2 gradient fields
  mobile_variant_2_gradient_angle?: string;
  mobile_variant_2_gradient_start_color?: string;
  mobile_variant_2_gradient_end_color?: string;
}

// Internal metadata structure used by components
interface SpecialisationMetadata {
  id: string;
  card_title: string;
  card_description: string;
  detail_title?: string;
  detail_subtitle?: string;
  detail_description?: string;
  icon?: string;
  icon_alt?: string;
  hover_icon?: string;
  hover_icon_alt?: string;
  priority?: number;
  meta_status?: boolean;
  // Desktop styling
  desktop_title_text_color?: string;
  desktop_active_card_color?: string;
  desktop_inactive_card_color?: string;
  desktop_description_gradient?: string;
  desktop_sub_title_text_color?: string;
  desktop_description_text_color?: string;
  desktop_active_title_text_color?: string;
  desktop_active_sub_title_text_color?: string;
  // Desktop gradient fields
  desktop_gradient_angle?: string;
  desktop_gradient_start_color?: string;
  desktop_gradient_end_color?: string;
  // Mobile Variant 1 (carousel) styling
  mobile_v1_icon?: string;
  mobile_v1_icon_alt?: string;
  mobile_v1_title_card_color?: string;
  mobile_v1_title_text_color?: string;
  mobile_v1_description_card_color?: string;
  mobile_v1_description_text_color?: string;
  // Mobile Variant 2 (accordion) styling
  mobile_v2_card_color?: string;
  mobile_v2_card_gradient?: string;
  mobile_v2_title_text_color?: string;
  mobile_v2_sub_title_text_color?: string;
  mobile_v2_description_text_color?: string;
  mobile_v2_accordion_element_color?: string;
  // Mobile Variant 2 gradient fields
  mobile_v2_gradient_angle?: string;
  mobile_v2_gradient_start_color?: string;
  mobile_v2_gradient_end_color?: string;
}

interface APICourseSpecialisationsData {
  id?: number;
  page_id?: number;
  component_id?: number;
  title: string;
  title_text_color?: string;
  desktop_background_color?: string;
  mobile_variant_1_background_color?: string;
  mobile_variant_2_background_color?: string;
  mobile_variant?: string; // "1" or "2"
  variant?: string;
  status?: number;
  // Global navigation icons for mobile variant 1 (carousel)
  mobile_variant_1_left_nav_icon?: string;
  mobile_variant_1_left_nav_icon_alt?: string;
  mobile_variant_1_right_nav_icon?: string;
  mobile_variant_1_right_nav_icon_alt?: string;
  // Global navigation icons for mobile variant 2 (accordion)
  mobile_variant_2_top_arrow_icon?: string;
  mobile_variant_2_top_arrow_icon_alt?: string;
  mobile_variant_2_bottom_arrow_icon?: string;
  mobile_variant_2_bottom_arrow_icon_alt?: string;
  metadata: APISpecialisationMetadata[];
}

interface CourseSpecialisationsProps {
  data: {
    data: APICourseSpecialisationsData;
  };
}

function CourseSpecialisation1({ data }: CourseSpecialisationsProps) {
  const isMobile = useIsMobile(768);
  const ref = useRef<HTMLElement>(null);

  const {
    title = "",
    title_text_color = "#101112",
    desktop_background_color = "#FFFFFF",
    mobile_variant_1_background_color = "#FFFFFF",
    mobile_variant_2_background_color = "#FFFFFF",
    mobile_variant = "2", // "1" = carousel, "2" = accordion
    // Global navigation icons for mobile variant 1 (carousel)
    mobile_variant_1_left_nav_icon,
    mobile_variant_1_left_nav_icon_alt,
    mobile_variant_1_right_nav_icon,
    mobile_variant_1_right_nav_icon_alt,
    // Global navigation icons for mobile variant 2 (accordion)
    mobile_variant_2_top_arrow_icon,
    mobile_variant_2_top_arrow_icon_alt,
    mobile_variant_2_bottom_arrow_icon,
    mobile_variant_2_bottom_arrow_icon_alt,
    metadata = [],
    status = 1,
  } = data?.data || {};

  // Track widget viewed event
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "course_specialisations_section");
  //   }
  // }, []);

  // Track card click for desktop
  const handleCardClick = useCallback(
    (index: number, item: SpecialisationMetadata) => {
      cardClick({
        section: "course_specialisations_section",
        component: "specialisation_card",
        label: item.card_title || item.detail_title || "",
        parent: title,
        redirectLink: "",
        pageKey: "",
      });
    },
    [title]
  );

  // Track navigation click for mobile carousel
  const handleCarouselNavigation = useCallback(
    (direction: "prev" | "next", currentIndex: number) => {
      navigationClick({
        section: "course_specialisations_section",
        component: "carousel_navigation",
        label: direction === "prev" ? "previous" : "next",
        index: String(currentIndex),
      });
    },
    []
  );

  // Track expansion click for mobile accordion
  const handleAccordionToggle = useCallback(
    (index: number, item: SpecialisationMetadata, isExpanded: boolean) => {
      expansionClick({
        section: "course_specialisations_section",
        component: "accordion_card",
        label: item.card_title || item.detail_title || "",
        index: String(index),
        entity_name: item.id,
      });
    },
    []
  );

  // Transform API metadata to internal format and filter/sort
  const displayData: SpecialisationMetadata[] = useMemo(() => {
    return (metadata || [])
      .filter((item: APISpecialisationMetadata) => item?.status !== false)
      .sort(
        (a: APISpecialisationMetadata, b: APISpecialisationMetadata) =>
          (a.priority || 0) - (b.priority || 0)
      )
      .map((item: APISpecialisationMetadata) => ({
        id: item.id,
        card_title: item.title,
        card_description: item.sub_title,
        detail_title: item.title,
        detail_subtitle: item.sub_title,
        detail_description: item.description,
        icon: item.desktop_icon,
        icon_alt: item.desktop_icon_alt,
        hover_icon: item.desktop_hover_icon,
        hover_icon_alt: item.desktop_hover_icon_alt,
        priority: item.priority,
        meta_status: item.status,
        // Desktop styling
        desktop_title_text_color: item.desktop_title_text_color,
        desktop_active_card_color: item.desktop_active_card_color,
        desktop_inactive_card_color: item.desktop_inactive_card_color,
        desktop_description_gradient: item.desktop_description_gradient,
        desktop_sub_title_text_color: item.desktop_sub_title_text_color,
        desktop_description_text_color: item.desktop_description_text_color,
        desktop_active_title_text_color: item.desktop_active_title_text_color,
        desktop_active_sub_title_text_color:
          item.desktop_active_sub_title_text_color,
        // Desktop gradient fields
        desktop_gradient_angle: item.desktop_gradient_angle,
        desktop_gradient_start_color: item.desktop_gradient_start_color,
        desktop_gradient_end_color: item.desktop_gradient_end_color,
        // Mobile Variant 1 (carousel) styling
        mobile_v1_icon: item.mobile_variant_1_icon,
        mobile_v1_icon_alt: item.mobile_variant_1_icon_alt,
        mobile_v1_title_card_color: item.mobile_variant_1_title_card_color,
        mobile_v1_title_text_color: item.mobile_variant_1_title_text_color,
        mobile_v1_description_card_color:
          item.mobile_variant_1_description_card_color,
        mobile_v1_description_text_color:
          item.mobile_variant_1_description_text_color,
        // Mobile Variant 2 (accordion) styling
        mobile_v2_card_color: item.mobile_variant_2_card_color,
        mobile_v2_card_gradient: item.mobile_variant_2_card_gradient,
        mobile_v2_title_text_color: item.mobile_variant_2_title_text_color,
        mobile_v2_sub_title_text_color:
          item.mobile_variant_2_sub_title_text_color,
        mobile_v2_description_text_color:
          item.mobile_variant_2_description_text_color,
        mobile_v2_accordion_element_color:
          item.mobile_variant_2_accordion_element_color,
        // Mobile Variant 2 gradient fields
        mobile_v2_gradient_angle: item.mobile_variant_2_gradient_angle,
        mobile_v2_gradient_start_color:
          item.mobile_variant_2_gradient_start_color,
        mobile_v2_gradient_end_color: item.mobile_variant_2_gradient_end_color,
      }));
  }, [metadata]);

  // Don't render if no data, status is disabled, or no metadata items
  if (!data?.data) {
    return null;
  }

  // Determine background color based on variant
  const mobileBackgroundColor =
    mobile_variant === "1"
      ? mobile_variant_1_background_color
      : mobile_variant_2_background_color;

  const commonProps = {
    title,
    titleTextColor: title_text_color,
    backgroundColor: isMobile
      ? mobileBackgroundColor
      : desktop_background_color,
    displayData,
  };

  if (isMobile) {
    // "1" = carousel, "2" = accordion
    if (mobile_variant === "1") {
      return (
        <CourseSpecialisationsCarousel
          {...commonProps}
          leftNavIcon={mobile_variant_1_left_nav_icon}
          leftNavIconAlt={mobile_variant_1_left_nav_icon_alt}
          rightNavIcon={mobile_variant_1_right_nav_icon}
          rightNavIconAlt={mobile_variant_1_right_nav_icon_alt}
          onNavigationClick={handleCarouselNavigation}
          showNavigationButtons={displayData?.length > 1}
          ref={ref}
        />
      );
    }
    return (
      <CourseSpecialisationsMobile
        {...commonProps}
        topArrowIcon={mobile_variant_2_top_arrow_icon}
        topArrowIconAlt={mobile_variant_2_top_arrow_icon_alt}
        bottomArrowIcon={mobile_variant_2_bottom_arrow_icon}
        bottomArrowIconAlt={mobile_variant_2_bottom_arrow_icon_alt}
        onCardToggle={handleAccordionToggle}
        ref={ref}
      />
    );
  }

  return (
    <CourseSpecialisationsDesktop
      {...commonProps}
      onCardClick={handleCardClick}
      ref={ref}
    />
  );
}

export default CourseSpecialisation1;
